/**
 * Pure helpers for the office job detail page (JOB-PAGE-REDESIGN.md).
 *
 * Three responsibilities, all client-side derivations over data the page
 * already fetches — no new endpoints:
 *   1. deriveStages    — the horizontal stage strip (Created → … → Closed)
 *   2. primaryHeaderActions — the two stage-contextual header buttons + the
 *      overflow menu contents
 *   3. groupTimeline   — turns the raw merged activity feed into a grouped,
 *      categorized, human-first story (audit noise behind an expander)
 */

import { JOB_STAGE_LABELS, OFFICE_STATUS_TRANSITIONS, TRANSITION_LABELS } from "../../lib/vocabulary";

// ── Shared shapes (structural — mirror the page's response types) ──────────

export interface StageJobLike {
    status: string;
    created_at: string;
    scheduled_start?: string | null;
    en_route_at?: string | null;
    arrived_at?: string | null;
    work_started_at?: string | null;
    completed_at?: string | null;
    trade_code?: string | null;
    /** Canonical job typing (backflow_test / backflow_repair_* /
     *  backflow_replace_*) — drives the "work" stage verb. */
    job_type_code?: string | null;
}

export interface StageInvoiceLike {
    status: string;
    total_amount?: number | string | null;
    created_at: string;
    /** Payment facts (ST import + native invoice_payments). */
    st_paid_on?: string | null;
    st_balance?: number | string | null;
    payments_total?: number | string | null;
    last_payment_at?: string | null;
}

export interface StageEstimateLike {
    status: string;
    created_at: string;
    updated_at?: string | null;
    /** ST "sold on" — best available sent/approved timestamp. */
    st_sold_on?: string | null;
}

export interface StageTestLike {
    performed_at: string;
}

export type StageState = "done" | "current" | "pending";

export type JobStageKey =
    | "created"
    | "scheduled"
    | "on_site"
    | "work"
    | "close_out"
    | "quote_sent"
    | "invoiced"
    | "paid"
    | "closed";

export interface JobStage {
    key: JobStageKey;
    label: string;
    ts: string | null;
    state: StageState;
    /** Extra annotation, e.g. "draft $80" on the invoice stage. */
    note?: string;
    /** Happening right now (the work stage of an in-progress job): the
     *  label is present tense ("Testing now") and there is no date yet. */
    live?: boolean;
}

export interface StageStrip {
    stages: JobStage[];
    /** Terminal off-track state rendered as a chip at the end of the strip. */
    terminal: "cancelled" | "held" | "cannot_complete" | null;
}

// ── 1. Stage derivation ────────────────────────────────────────────────────

// Job statuses mapped to the stage KEY the job currently "sits at"
// (draft/awaiting point at Scheduled — that's the next step the office is
// working toward). Key-based because the stage list length varies (Quote
// sent only renders when an estimate exists).
const STATUS_STAGE_KEY: Record<string, JobStageKey> = {
    draft: "scheduled",
    awaiting_scheduling: "scheduled",
    unqueued: "scheduled",
    scheduled: "scheduled",
    en_route: "on_site",
    arrived: "on_site",
    in_progress: "work",
    office_review: "close_out",
    // completed is NOT here: it sits at whichever money stage still lacks
    // evidence (see deriveStages). A static "invoiced" lit up Invoiced as
    // current on a job whose invoice was paid weeks ago (audit 2026-10-01).
    closed: "closed",
};

function maxIso(values: Array<string | null | undefined>): string | null {
    let best: string | null = null;
    for (const v of values) {
        if (!v) continue;
        if (!best || new Date(v).getTime() > new Date(best).getTime()) best = v;
    }
    return best;
}

function money(value: number): string {
    return `$${Math.round(value)}`;
}

/**
 * The verb for what a job DOES to its devices — used for the "work" stage
 * and any results heading. A replacement job "Replaced", a repair "Repaired",
 * a plain backflow test "Tested"; anything else "Work done". Keys off the
 * canonical job_type_code (backflow_replace_* / backflow_repair_* /
 * backflow_test) and falls back to trade_code, so a job with no type set
 * keeps the old backflow="Tested" behavior.
 */
export function workVerb(job: { job_type_code?: string | null; trade_code?: string | null }): string {
    const type = (job.job_type_code ?? "").toLowerCase();
    if (type.includes("replace")) return "Replaced";
    if (type.includes("repair")) return "Repaired";
    return job.trade_code === "backflow" ? "Tested" : "Work done";
}

/** Present-tense twin of workVerb, for the work stage while the tech is
 *  still on it: "Tested" would claim the devices are done. */
function workVerbNow(job: { job_type_code?: string | null; trade_code?: string | null }): string {
    const past = workVerb(job);
    if (past === "Replaced") return "Replacing now";
    if (past === "Repaired") return "Repairing now";
    if (past === "Tested") return "Testing now";
    return "Working now";
}

export function deriveStages(
    job: StageJobLike,
    testResults: StageTestLike[] = [],
    invoices: StageInvoiceLike[] = [],
    estimates: StageEstimateLike[] = [],
): StageStrip {
    // The "work" stage verb follows what the job actually does — a
    // replacement reads "Replaced", a repair "Repaired", a test "Tested".
    const workLabel = workVerb(job);

    const lastTestAt = maxIso(testResults.map((t) => t.performed_at));
    // completed_at is stamped when the job reaches office_review OR completed,
    // so it doubles as the "entered close out" timestamp.
    const reviewAt = job.completed_at ?? null;
    // Invoiced = the invoice was issued. The enum is draft | open |
    // partially_paid | paid | void (014_commercial_documents.sql). "open" is
    // a FINALIZED, unpaid invoice — finalizing sets it (customerSend.ts), an
    // ST "Posted"/"Exported" invoice imports as it (serviceTitanMappers/
    // invoices.ts) — so it counts. Whether it was emailed is a separate fact
    // (the send ledger, #1142). Only a draft is not yet invoiced; a void one
    // never was.
    const sentInvoice = invoices.find((i) => !["draft", "void"].includes((i.status ?? "").toLowerCase()));
    const draftInvoice = invoices.find((i) => (i.status ?? "").toLowerCase() === "draft");
    const draftNote = draftInvoice
        ? `draft${draftInvoice.total_amount != null ? ` ${money(Number(draftInvoice.total_amount))}` : ""}`
        : undefined;

    // Quote sent (only when an estimate exists): sent/accepted = done.
    const sentEstimate = estimates.find((e) => ["sent", "accepted"].includes((e.status ?? "").toLowerCase()));
    const approvedEstimate = estimates.find((e) => (e.status ?? "").toLowerCase() === "accepted");

    // Paid: fully paid = done; partially paid = pending with "$X of $Y".
    const paidInvoice = invoices.find((i) => (i.status ?? "").toLowerCase() === "paid");
    const partialInvoice = invoices.find((i) => (i.status ?? "").toLowerCase() === "partially_paid");
    let partialNote: string | undefined;
    if (partialInvoice && partialInvoice.total_amount != null) {
        const total = Number(partialInvoice.total_amount);
        const fromPayments = Number(partialInvoice.payments_total ?? 0);
        const fromBalance = partialInvoice.st_balance != null ? total - Number(partialInvoice.st_balance) : Number.NaN;
        const paidAmount = fromPayments > 0 ? fromPayments : fromBalance;
        if (Number.isFinite(paidAmount) && paidAmount > 0) {
            partialNote = `${money(paidAmount)} of ${money(total)}`;
        } else {
            partialNote = "partial";
        }
    }

    const closed = job.status === "closed";

    const raw: Array<Omit<JobStage, "state">> = [
        { key: "created", label: JOB_STAGE_LABELS.created, ts: job.created_at ?? null },
        { key: "scheduled", label: JOB_STAGE_LABELS.scheduled, ts: job.scheduled_start ?? null },
        { key: "on_site", label: JOB_STAGE_LABELS.on_site, ts: job.arrived_at ?? job.en_route_at ?? null },
        // Test results are the only completion evidence — work_started_at
        // means the tech began, not finished (a 0-of-3 job read "Tested").
        { key: "work", label: workLabel, ts: lastTestAt },
        { key: "close_out", label: JOB_STAGE_LABELS.close_out, ts: reviewAt },
        // Quote sent renders only when the job actually has an estimate —
        // jobs without one keep the short strip (spec §6b.2).
        ...(estimates.length > 0
            ? [
                  {
                      key: "quote_sent" as const,
                      label: JOB_STAGE_LABELS.quote_sent,
                      ts: sentEstimate ? (sentEstimate.st_sold_on ?? sentEstimate.updated_at ?? null) : null,
                      note: approvedEstimate ? "approved" : sentEstimate ? undefined : "draft",
                  },
              ]
            : []),
        {
            key: "invoiced",
            label: JOB_STAGE_LABELS.invoiced,
            ts: sentInvoice?.created_at ?? null,
            note: sentInvoice ? undefined : draftNote,
        },
        {
            key: "paid",
            label: JOB_STAGE_LABELS.paid,
            ts: paidInvoice ? (paidInvoice.st_paid_on ?? paidInvoice.last_payment_at ?? null) : null,
            note: paidInvoice ? undefined : partialNote,
        },
        { key: "closed", label: JOB_STAGE_LABELS.closed, ts: closed ? (job.completed_at ?? null) : null },
    ];

    // Evidence a stage actually happened.
    const evidence = raw.map((s) => {
        if (s.key === "quote_sent") return !!sentEstimate;
        if (s.key === "invoiced") return !!sentInvoice;
        if (s.key === "paid") return !!paidInvoice;
        if (s.key === "closed") return closed;
        return !!s.ts;
    });

    // Current = the stage the status maps to; for off-track statuses (held,
    // cannot_complete, cancelled) fall back to the first stage without
    // evidence. A closed job has no current stage — everything is done.
    const statusKey = STATUS_STAGE_KEY[job.status];
    let currentIdx: number;
    if (job.status === "completed") {
        // Work is done; the job sits at the first money stage still missing
        // its evidence (Invoiced if not issued, else Paid if unpaid). Paid in
        // full → nothing is current: every stage up to Paid shows done and
        // only Closed waits.
        currentIdx = raw.findIndex((s, idx) => (s.key === "invoiced" || s.key === "paid") && !evidence[idx]);
    } else {
        currentIdx = statusKey ? raw.findIndex((s) => s.key === statusKey) : evidence.findIndex((e) => !e);
        if (currentIdx === -1) currentIdx = raw.length - 1;
    }
    // Everything before this index happened. With no current stage (a paid
    // completed job) that is everything before Closed.
    const doneBefore = currentIdx === -1 ? raw.length - 1 : currentIdx;

    const stages: JobStage[] = raw.map((s, idx) => {
        if (closed) return { ...s, state: "done" as const };
        if (idx === currentIdx && s.key === "work" && job.status === "in_progress") {
            // The tech is on it now. A date here would read as "finished on".
            return { ...s, label: workVerbNow(job), ts: null, live: true, state: "current" as const };
        }
        if (idx === currentIdx) return { ...s, state: "current" as const };
        if (evidence[idx] || idx < doneBefore) return { ...s, state: "done" as const };
        return { ...s, state: "pending" as const };
    });

    const terminal =
        job.status === "cancelled"
            ? "cancelled"
            : job.status === "held"
              ? "held"
              : job.status === "cannot_complete"
                ? "cannot_complete"
                : null;

    return { stages, terminal };
}

/** True once the visit has happened — flips the rail to "report mode"
 *  (Schedule & Crew collapses, Customer & Site leads). */
export function isPostVisit(status: string): boolean {
    return ["in_progress", "office_review", "completed", "closed", "cancelled"].includes(status);
}

// ── 2. Contextual header actions ───────────────────────────────────────────

export type HeaderAction =
    | { kind: "status"; status: string; label: string; tone?: "default" | "outline" }
    | { kind: "link"; to: string; label: string; icon?: "edit" | "estimate" | "invoice" | "review" }
    | { kind: "cancel"; label: string }
    // #815: "Send back to field" stopped being a bare status flip — it now
    // requires a why-note and messages the tech (task + push + SMS) through
    // POST /api/jobs/:id/send-back-to-field.
    | { kind: "send-back"; label: string }
    // #937: reopening a cancelled job isn't a status flip either — `cancelled`
    // has no outgoing edges in the matrix on purpose. POST /api/jobs/:id/reopen
    // clears the cancellation columns and checks for a live duplicate first.
    | { kind: "reopen"; label: string };

export interface HeaderActionsCtx {
    jobId: string;
    customerId?: string | null;
    /** The job's site — carried into the Create estimate/invoice links so the
     *  form locks the location and the estimate is born with its site (#1024:
     *  a site-less estimate can never link devices to its lines). */
    siteId?: string | null;
    hasInvoices: boolean;
}

export interface HeaderActions {
    primary: HeaderAction[];
    overflow: HeaderAction[];
}

// Office-legal transitions + labels come from the shared vocabulary module
// (parity-tested against src/server/services/jobStatusMachine.ts, so the UI
// can never offer a move the server rejects).
const TRANSITIONS = OFFICE_STATUS_TRANSITIONS;

function statusAction(target: string, label?: string): HeaderAction {
    return { kind: "status", status: target, label: label ?? TRANSITION_LABELS[target] ?? target.replace(/_/g, " ") };
}

/**
 * Two stage-contextual primaries; everything else (still legal / still
 * useful) goes to the ⋯ overflow so no capability is lost (spec §3.1).
 */
export function primaryHeaderActions(status: string, ctx: HeaderActionsCtx): HeaderActions {
    const primary: HeaderAction[] = [];
    const newDocLink = (kind: "estimate" | "invoice"): HeaderAction => ({
        kind: "link",
        to:
            `/commercial/new?jobId=${ctx.jobId}&customerId=${ctx.customerId ?? ""}` +
            (ctx.siteId ? `&siteId=${ctx.siteId}` : "") +
            `&kind=${kind}`,
        label: kind === "estimate" ? "Create estimate" : "Create invoice",
        icon: kind,
    });

    switch (status) {
        case "draft":
            primary.push(statusAction("scheduled"), statusAction("awaiting_scheduling"));
            break;
        case "awaiting_scheduling":
            primary.push(statusAction("scheduled"), statusAction("held"));
            break;
        case "scheduled":
            primary.push(statusAction("in_progress"), statusAction("held"));
            break;
        case "en_route":
            primary.push(statusAction("arrived"), statusAction("in_progress"));
            break;
        case "arrived":
            primary.push(statusAction("in_progress"), statusAction("cannot_complete"));
            break;
        case "in_progress":
            primary.push(statusAction("office_review"), statusAction("held"));
            break;
        case "office_review":
            primary.push(
                { kind: "link", to: "/office-review", label: "Review & approve", icon: "review" },
                // #759 (audit J3): this transition is office_review → in_progress,
                // i.e. hand the job back to the tech — "Reopen" on a job that was
                // never closed read as a bug. (completed → office_review keeps
                // its legitimate "Reopen" below.)
                // #815: no longer a bare status flip — the send-back kind
                // demands a reason and delivers it to the tech.
                { kind: "send-back", label: "Send back to field" },
            );
            break;
        case "cannot_complete":
            primary.push(statusAction("scheduled", "Reschedule"), statusAction("office_review"));
            break;
        case "held":
            primary.push(statusAction("in_progress", "Resume"), statusAction("scheduled"));
            break;
        case "completed":
            primary.push(statusAction("office_review", "Reopen"));
            if (!ctx.hasInvoices) primary.push(newDocLink("invoice"));
            break;
        case "closed":
            if (!ctx.hasInvoices) primary.push(newDocLink("invoice"));
            break;
        case "cancelled":
            // #937: the one action a cancelled job has. Before this it had
            // none at all — the ⋯ menu rendered essentially empty, which read
            // as a broken page rather than a deliberate dead end.
            primary.push({ kind: "reopen", label: "Reopen job" });
            break;
        default:
            break; // unknown statuses get no primaries
    }

    const promotedStatuses = new Set(
        primary.filter((a): a is Extract<HeaderAction, { kind: "status" }> => a.kind === "status").map((a) => a.status),
    );
    const promotedLinks = new Set(primary.filter((a) => a.kind === "link").map((a) => a.label));

    const overflow: HeaderAction[] = [{ kind: "link", to: `/jobs/${ctx.jobId}/edit`, label: "Edit Job", icon: "edit" }];
    // #1110: always offered — a job with one estimate still needs a revised or
    // second quote, and hiding the action made that impossible from this page.
    overflow.push(newDocLink("estimate"));
    if (!ctx.hasInvoices && !promotedLinks.has("Create invoice")) overflow.push(newDocLink("invoice"));
    // Remaining legal transitions the primaries didn't claim.
    for (const target of TRANSITIONS[status] ?? []) {
        if (!promotedStatuses.has(target)) overflow.push(statusAction(target));
    }
    if (status !== "cancelled") overflow.push({ kind: "cancel", label: "Cancel Job" });

    return { primary: primary.slice(0, 2), overflow };
}

// ── 3. Activity feed grouping ──────────────────────────────────────────────

export interface TimelineItemLike {
    id: string;
    type: string;
    title: string;
    detail?: string;
    actor?: string;
    timestamp: string;
    meta?: Record<string, unknown>;
    /** Raw audit action (server includes it for audit rows). */
    action?: string;
}

export type FeedCategory = "note" | "comms" | "files" | "milestone" | "system";

export interface FeedEntry {
    id: string;
    category: FeedCategory;
    title: string;
    detail?: string;
    actor?: string;
    timestamp: string;
    /** The raw items this entry represents (1 for ungrouped). */
    items: TimelineItemLike[];
}

const GROUP_WINDOW_MS = 5 * 60 * 1000;

// Audit actions that read as milestones (part of the job's story) rather
// than system noise.
const MILESTONE_ACTIONS = new Set([
    "job.create",
    "job.status",
    "job.cancel",
    "office_review.approve",
    "office_review.approve_and_send",
    "office_review.hold",
    "office_review.return",
]);

// #1545: `customer.packet_send` fires on every customer packet — the test
// report, the invoice, the photos — and used to fall through to "system"
// noise, so the emails this job actually sent were the one thing the Comms
// filter did not show. `office_review.approve_and_send` stays a MILESTONE on
// purpose: it is the approval, and it fires alongside the packet event, so
// listing both here would double every send.
const COMMS_ACTIONS = new Set([
    "job.message_customer",
    "job.sms.on_my_way",
    "customer.packet_send",
    // 2026-10-07: a packet delivered by portal, mail or in person (no email).
    "customer.delivered_another_way",
]);

function auditAction(item: TimelineItemLike): string | null {
    if (!item.id.startsWith("audit:")) return null;
    if (typeof item.action === "string") return item.action;
    // Server translators echo the raw action into meta (activity.ts).
    const metaAction = item.meta?.action;
    return typeof metaAction === "string" ? metaAction : null;
}

/** Bucket one raw timeline item into a feed category. Keys off the synthetic
 *  id prefixes and (for audit rows) the raw action — never the title text. */
export function classifyTimelineItem(item: TimelineItemLike): FeedCategory {
    const id = item.id;
    // Job notes are their own rows off `job_notes` (#1377) — they are the
    // whole point of the Notes filter, never system noise.
    if (id.startsWith("note:")) return "note";
    if (id.startsWith("call:")) return "comms";
    if (id.startsWith("attach:")) return "files";
    if (id.startsWith("test:") || id.startsWith("repair:") || id.startsWith("firesafety:")) return "milestone";
    if (id.startsWith("lifecycle:")) return "milestone";
    const action = auditAction(item);
    if (action) {
        if (COMMS_ACTIONS.has(action)) return "comms";
        if (MILESTONE_ACTIONS.has(action)) return "milestone";
        if (action === "job.update") {
            // Notes-only saves read as notes; everything else is system noise
            // (the milestone version of the same save is the job.status row).
            const fields = Array.isArray(item.meta?.fields) ? (item.meta!.fields as unknown[]) : [];
            const strs = fields.filter((f): f is string => typeof f === "string");
            if (
                strs.length > 0 &&
                strs.every((f) => f === "notes" || f === "notes_rich" || f === "tech_instructions")
            ) {
                return "note";
            }
            return "system";
        }
        return "system";
    }
    // Estimate / invoice / billing rows are part of the money story.
    if (item.type === "estimate" || item.type === "invoice" || item.type === "billing") return "milestone";
    return "system";
}

function ts(item: TimelineItemLike): number {
    return new Date(item.timestamp).getTime();
}

function plural(n: number, one: string, many?: string): string {
    return `${n} ${n === 1 ? one : (many ?? `${one}s`)}`;
}

/** Title for a grouped run of attachment uploads, using the server's
 *  file-type-aware labels ("Report generated", "Photo uploaded", …). */
function fileGroupTitle(items: TimelineItemLike[]): string {
    const reports = items.filter((i) => /report/i.test(i.title)).length;
    const photos = items.filter((i) => /photo/i.test(i.title)).length;
    if (reports === items.length) return `Uploaded ${plural(items.length, "report PDF")}`;
    if (photos === items.length) return `Uploaded ${plural(items.length, "photo")}`;
    return `Uploaded ${plural(items.length, "file")}`;
}

/** Title for a grouped burst of job.update / job.status audit rows — one
 *  office save that fanned out into several audit entries. */
function updateGroupTitle(items: TimelineItemLike[]): { title: string; detail?: string } {
    const changed = new Set<string>();
    let statusTo: string | null = null;
    for (const item of items) {
        const action = auditAction(item);
        if (action === "job.status") {
            const to = typeof item.meta?.to === "string" ? (item.meta.to as string) : null;
            if (to) statusTo = to;
            changed.add("status");
        } else if (action === "job.update") {
            const fields = Array.isArray(item.meta?.fields) ? (item.meta!.fields as unknown[]) : [];
            for (const f of fields) if (typeof f === "string") changed.add(f.replace(/_/g, " "));
        }
    }
    const list = Array.from(changed);
    const title = statusTo ? `Updated the job — now ${statusTo.replace(/_/g, " ")}` : "Updated the job";
    return { title, detail: list.length > 0 ? `Changed ${list.join(", ")}` : undefined };
}

function isUpdateBurstItem(item: TimelineItemLike): boolean {
    const action = auditAction(item);
    return action === "job.update" || action === "job.status";
}

/**
 * Sort newest-first, then merge consecutive runs (same actor, same kind of
 * event, ≤5 minutes apart) into single entries:
 *   - attachment uploads → "Uploaded 3 report PDFs"
 *   - job.update / job.status bursts → "Updated the job — …: status, schedule"
 * Everything else passes through 1:1 with its category.
 */
export function groupTimeline(items: TimelineItemLike[]): FeedEntry[] {
    const sorted = [...items].sort((a, b) => ts(b) - ts(a));
    const entries: FeedEntry[] = [];
    let i = 0;
    while (i < sorted.length) {
        const head = sorted[i];
        const headCat = classifyTimelineItem(head);
        const run: TimelineItemLike[] = [head];

        const sameRun = (candidate: TimelineItemLike): boolean => {
            if ((candidate.actor ?? "") !== (head.actor ?? "")) return false;
            if (Math.abs(ts(run[run.length - 1]) - ts(candidate)) > GROUP_WINDOW_MS) return false;
            if (head.id.startsWith("attach:")) return candidate.id.startsWith("attach:");
            if (isUpdateBurstItem(head)) return isUpdateBurstItem(candidate);
            return false;
        };

        let j = i + 1;
        while (j < sorted.length && sameRun(sorted[j])) {
            run.push(sorted[j]);
            j++;
        }

        if (run.length > 1 && head.id.startsWith("attach:")) {
            entries.push({
                id: `group:${head.id}`,
                category: "files",
                title: fileGroupTitle(run),
                detail: run
                    .map((r) => r.detail)
                    .filter(Boolean)
                    .slice(0, 3)
                    .join(", "),
                actor: head.actor,
                timestamp: head.timestamp,
                items: run,
            });
        } else if (run.length > 1 && isUpdateBurstItem(head)) {
            const { title, detail } = updateGroupTitle(run);
            // A burst that includes a status change is a milestone; a pure
            // field-shuffle stays system noise.
            const hasStatus = run.some((r) => auditAction(r) === "job.status");
            entries.push({
                id: `group:${head.id}`,
                category: hasStatus ? "milestone" : "system",
                title,
                detail,
                actor: head.actor,
                timestamp: head.timestamp,
                items: run,
            });
        } else {
            entries.push({
                id: head.id,
                category: headCat,
                title: head.title,
                detail: head.detail,
                actor: head.actor,
                timestamp: head.timestamp,
                items: run,
            });
        }
        i = j;
    }
    return entries;
}

export function countSystemEntries(entries: FeedEntry[]): number {
    return entries.filter((e) => e.category === "system").length;
}

// ── 4. Story chapters (Issue #768 — state-aware receipts/ghosts) ───────────
//
// The left column renders as stage-owned chapters: a finished chapter
// compresses to a one-line receipt ("✓ Scheduled — Aug 6, 8:00 AM · Brian
// Jr"), the chapter the job currently sits in is open, and a chapter whose
// stage hasn't happened yet renders as a muted ghost row ("◻ Results — visit
// hasn't happened yet"). This derivation is pure so the per-status behavior
// is unit-testable; detail.tsx just renders the plan.

export type ChapterKey = "schedule" | "results" | "paperwork";
export type ChapterState = "receipt" | "open" | "ghost";

export interface ChapterPlan {
    key: ChapterKey;
    state: ChapterState;
    /** The one-line receipt (✓) or ghost (◻) text. Empty for open chapters. */
    line: string;
    /** Receipt expansion body — the schedule chapter's booking/visit facts. */
    detail: string[];
}

export interface ChapterJobLike extends StageJobLike {
    assigned_to_name?: string | null;
    scheduled_end?: string | null;
    /** Booking lock — set once dispatch confirms the appointment. */
    confirmed_at?: string | null;
    confirmed_by_name?: string | null;
}

export interface ChapterFacts {
    deviceCount: number;
    testCount: number;
    invoiceCount: number;
    estimateCount: number;
    /** Every attachment the paperwork card lists (docs + generated reports). */
    documentCount: number;
}

function chapterStamp(iso: string, withTime: boolean): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (!withTime) return day;
    return `${day}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function timeOnly(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function deriveChapters(job: ChapterJobLike, facts: ChapterFacts): ChapterPlan[] {
    const postVisit = isPostVisit(job.status);

    // Schedule: the plan/briefing content lives in the NOW panel and the rail
    // (Schedule & Crew card), so this chapter is always a compressed line —
    // a receipt once there's an appointment (or a visit), a ghost until then.
    const tech = job.assigned_to_name ? ` · ${job.assigned_to_name}` : "";
    let schedule: ChapterPlan;
    if (postVisit && (job.arrived_at || job.completed_at)) {
        const start = job.arrived_at ?? job.work_started_at;
        const end = job.completed_at;
        const window =
            start && end ? `${chapterStamp(start, true)}–${timeOnly(end)}` : chapterStamp((start ?? end)!, true);
        schedule = { key: "schedule", state: "receipt", line: `Visited ${window}${tech}`, detail: [] };
    } else if (job.scheduled_start) {
        schedule = {
            key: "schedule",
            state: "receipt",
            line: `Scheduled — ${chapterStamp(job.scheduled_start, true)}${tech}`,
            detail: [],
        };
    } else {
        schedule = { key: "schedule", state: "ghost", line: "Not scheduled yet", detail: [] };
    }
    // Receipt expansion: the booking/visit facts, in order, only what exists.
    if (schedule.state === "receipt") {
        const lines: string[] = [`Created ${chapterStamp(job.created_at, false)}`];
        if (job.scheduled_start) {
            const end = job.scheduled_end ? `–${timeOnly(job.scheduled_end)}` : "";
            lines.push(`Booked for ${chapterStamp(job.scheduled_start, true)}${end}`);
        }
        if (job.confirmed_at) {
            lines.push(
                `Booking confirmed ${chapterStamp(job.confirmed_at, false)}${job.confirmed_by_name ? ` by ${job.confirmed_by_name}` : ""}`,
            );
        }
        if (job.en_route_at) lines.push(`En route ${timeOnly(job.en_route_at)}`);
        if (job.arrived_at) lines.push(`On site ${timeOnly(job.arrived_at)}`);
        if (job.work_started_at) lines.push(`Work started ${timeOnly(job.work_started_at)}`);
        if (job.completed_at) lines.push(`Wrapped ${chapterStamp(job.completed_at, true)}`);
        schedule.detail = lines;
    }

    // Results: open once the visit happened (or any result already exists —
    // e.g. a portal-filed test on a job that never went through our statuses);
    // a ghost before that. The ghost still expands to the full card so the
    // office can build the device list pre-visit.
    const results: ChapterPlan =
        postVisit || facts.testCount > 0
            ? { key: "results", state: "open", line: "", detail: [] }
            : {
                  key: "results",
                  state: "ghost",
                  line: `Results — visit hasn't happened yet${
                      facts.deviceCount > 0
                          ? ` · ${facts.deviceCount} device${facts.deviceCount === 1 ? "" : "s"} expected`
                          : ""
                  }`,
                  detail: [],
              };

    // Paperwork: open once anything exists to show (documents, reports,
    // invoices, estimates) or the visit happened; a ghost before that
    // (expandable, so upload stays reachable).
    const hasPaper = facts.invoiceCount + facts.estimateCount + facts.documentCount > 0;
    const paperwork: ChapterPlan =
        postVisit || hasPaper
            ? { key: "paperwork", state: "open", line: "", detail: [] }
            : { key: "paperwork", state: "ghost", line: "Paperwork — nothing yet", detail: [] };

    return [schedule, results, paperwork];
}

/** Which chapter a stage-strip pill belongs to — pill click scrolls to AND
 *  expands that chapter (Issue #768). */
export const STAGE_CHAPTER: Record<JobStageKey, ChapterKey> = {
    created: "schedule",
    scheduled: "schedule",
    on_site: "schedule",
    work: "results",
    close_out: "paperwork",
    quote_sent: "paperwork",
    invoiced: "paperwork",
    paid: "paperwork",
    closed: "paperwork",
};

/**
 * The subject line a resend opens with (#1545).
 *
 * Mirrors the shape the packet composer already sends under — the customer's
 * name and the place the work happened — because a resend landing in the same
 * inbox under a different subject reads as a different job. The office can edit
 * it freely before sending; this is the starting point, not a rule.
 *
 * Falls back down the chain it can actually rely on: site name → site address →
 * job title → the job number. There is always a job number, so this never
 * returns an empty subject, which would send as "(no subject)".
 */
export function packetSubjectFor(job: {
    company_name?: string | null;
    site_name?: string | null;
    site_address?: string | null;
    title?: string | null;
    display_number?: string | null;
    id: string;
}): string {
    const where = (job.site_name || job.site_address || job.title || "").trim();
    const who = (job.company_name || "").trim();
    const jobRef = (job.display_number || job.id.slice(0, 8)).trim();
    const head = [who, where].filter(Boolean).join(" — ");
    return head ? `${head} · job ${jobRef}` : `Job ${jobRef}`;
}
